import type {
  ConstructionNoteMediaReference,
  PhotoCaptionEditContext,
  WalkPhotoConvertTarget,
  WalkPhotoData,
  WalkPhotoDestination,
  WalkPhotoHomeKind,
} from "../../../shared/types";

/**
 * Walk photos review (Claude Design "Walk Photos Options" 5a): the last review
 * step. "To review" is only the photos on a pending daily-log Photos card —
 * the catch-all, the one list that needs a last look for misfires. Every
 * photo there starts SELECTED and syncs; tapping one deselects it — it stays
 * in Led AI, NOT SYNCING. "Already synced" is read-only history: the photos
 * confirmed earlier on other records, in time order.
 *
 * Deselections stay a local draft until Send. Description and location edits
 * save immediately, and so does Convert: the new record opens in the review
 * stack so its fields can be filled in, and the photo leaves To review.
 */

export type WalkPhotosDraft = {
  /** Photos to delete outright. The phone no longer offers this (5a keeps
   *  every photo); the field stays for the submission's shape. */
  removed: Record<string, true>;
  /** Deselected: kept in Led AI, not sent to the integration. */
  excluded: Record<string, true>;
};

/**
 * The project's integration, which names everything on this step (LED-766).
 * Procore keeps its words; Autodesk speaks in its two records — the daily
 * report and the Issue — and has no task (`AUTODESK_RECORD_CONFIG.task` is
 * off, so a task never reaches ACC). Unlinked projects save locally using
 * the same card layout without provider sync labels.
 */
export type WalkPhotosIntegration = "procore" | "autodesk" | "local";

export function walkPhotosProviderName(integration: WalkPhotosIntegration): "Procore" | "Autodesk" | "Led AI" {
  return integration === "local" ? "Led AI" : integration === "autodesk" ? "Autodesk" : "Procore";
}

/** "daily log" (Procore) / "daily report" (Autodesk). */
export function walkPhotosLogNoun(integration: WalkPhotosIntegration): string {
  return integration === "autodesk" ? "daily report" : "daily log";
}

export const EMPTY_WALK_PHOTOS_DRAFT: WalkPhotosDraft = { removed: {}, excluded: {} };

export type WalkPhotoChip = { label: string; tone: WalkPhotoChipTone };
export type WalkPhotoChipTone = "log" | "task" | "observation" | "punch" | "plain";

export type WalkPhotoRow = {
  photo: WalkPhotoData;
  removed: boolean;
  /** Deselected on To review: NOT SYNCING. */
  excluded: boolean;
  /** Confirmed earlier in review — the green check next to the time. */
  reviewed: boolean;
  chip: WalkPhotoChip;
  /** Kinds this row can still be converted to (empty = no Convert entry). */
  convertOptions: WalkPhotoConvertTarget[];
  /** Whether "Remove photo" is offered: still under review, or homeless. A
   *  photo an accepted record carries may already be in Procore, and the
   *  server refuses to delete it (`submitWalkPhotoReview`). */
  removable: boolean;
};

export const CONVERT_LABEL: Record<WalkPhotoConvertTarget, string> = {
  task: "Task",
  observation: "Observation",
  punch: "Punch item",
};

/** Under Autodesk both become an Issue; the subtype is what tells them apart
 *  (the deck's own convert menu says the same: `VisitReviewStack`). */
const AUTODESK_CONVERT_LABEL: Partial<Record<WalkPhotoConvertTarget, string>> = {
  observation: "Issue · Observation",
  punch: "Issue · Punch List",
};

export function convertLabel(target: WalkPhotoConvertTarget, integration: WalkPhotosIntegration = "procore"): string {
  return (integration === "autodesk" && AUTODESK_CONVERT_LABEL[target]) || CONVERT_LABEL[target];
}

/** "Convert to task, observation or punch" / "Convert to an issue". */
export function convertPrompt(integration: WalkPhotosIntegration = "procore"): string {
  return integration === "autodesk" ? "Convert to an issue" : "Convert to task, observation or punch";
}

export const CHIP_TONE_OF: Record<WalkPhotoConvertTarget, WalkPhotoChipTone> = {
  task: "task",
  observation: "observation",
  punch: "punch",
};

const CONVERT_TARGETS: WalkPhotoConvertTarget[] = ["task", "observation", "punch"];

/** No Task under Autodesk: it could never sync. */
export function convertTargetsFor(integration: WalkPhotosIntegration = "procore"): WalkPhotoConvertTarget[] {
  return integration === "autodesk" ? CONVERT_TARGETS.filter((t) => t !== "task") : CONVERT_TARGETS;
}

/** A photo still under review: it sits on a Photos card nobody has accepted. */
export function isReviewablePhoto(photo: WalkPhotoData): boolean {
  return photo.home?.kind === "photos" && !photo.home.approved;
}

export function walkPhotoChip(
  kind: WalkPhotoHomeKind | null,
  category?: string,
  integration: WalkPhotosIntegration = "procore",
  syncExcluded = false,
): WalkPhotoChip {
  const acc = integration === "autodesk";
  const log = acc ? "DAILY REPORT" : "DAILY LOG";
  switch (kind) {
    case "photos":
      return { label: `${log} · PHOTOS`, tone: "log" };
    case "log":
      // Under Autodesk an observation-category daily-log item is not part of
      // the daily report: it is published as an Issue (autodeskSync routes
      // observations to Issues), so that is what it is called here.
      if (acc && category === "observations") return { label: "ISSUE", tone: "observation" };
      return {
        label: `${log} · ${(category ?? "notes").replace(/_/g, " ").toUpperCase()}`,
        tone: "log",
      };
    case "task":
      // ACC has two records — the daily report and the Issue — and no task
      // (tasks are never generated on an Autodesk walk). Never name a third
      // kind: a task-held photo there reads like any photo with no ACC record.
      if (acc) return { label: "PHOTOS", tone: "plain" };
      return { label: "TASK", tone: "task" };
    case "observation":
      return { label: acc ? "ISSUE" : "OBSERVATION", tone: "observation" };
    case "punch":
      return { label: acc ? "ISSUE" : "PUNCH", tone: "punch" };
    default:
      return syncExcluded ? { label: "NOT SYNCING", tone: "plain" } : { label: "PHOTOS", tone: "plain" };
  }
}

/**
 * `reviewCardIds` — the Photos cards this screen reviews, when the caller
 * knows them. A walk can hold a pending Photos card that is NOT this step's:
 * an older card (LED-711, `isWalkPhotosStepCard`) the reviewer put Later in
 * the deck is still pending on the server, so `getWalkPhotos` reports its
 * photos as under review. Here they are not: not To review, not selectable,
 * not converted, not counted — and Send leaves that card alone
 * (`submitWalkPhotoReview`'s `photoCardIds`). Absent, every pending Photos
 * card is this screen's, as before.
 */
export function buildWalkPhotoRows(
  photos: WalkPhotoData[],
  draft: WalkPhotosDraft,
  reviewCardIds?: ReadonlySet<string> | null,
  integration: WalkPhotosIntegration = "procore",
): {
  toReview: WalkPhotoRow[];
  /** Already synced: confirmed earlier on a record, time order. Read-only. */
  synced: WalkPhotoRow[];
  all: WalkPhotoRow[];
  /** Photos still on To review (selected or not). */
  reviewCount: number;
  /** Of those, the ones Send will carry to Procore. */
  selectedCount: number;
  total: number;
} {
  const excludedSet = draft.excluded ?? {};
  const underReview = (photo: WalkPhotoData) =>
    isReviewablePhoto(photo) && (!reviewCardIds || reviewCardIds.has(photo.home!.itemId));
  const all = [...photos]
    .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0))
    .map<WalkPhotoRow>((photo) => ({
      photo,
      removed: !!draft.removed[photo.mediaId],
      excluded: underReview(photo) && !!excludedSet[photo.mediaId],
      reviewed: !!photo.home?.approved,
      chip: walkPhotoChip(photo.home?.kind ?? null, photo.home?.category, integration, !!photo.syncExcluded),
      // Only a photo on a pending Photos card moves. A record accepted
      // earlier may already be in Procore; re-typing it belongs in its own
      // review, not here.
      convertOptions: underReview(photo) ? convertTargetsFor(integration) : [],
      removable: !photo.home || underReview(photo),
    }));
  const toReview = all.filter((row) => underReview(row.photo));
  const live = toReview.filter((row) => !row.removed);
  return {
    toReview,
    synced: all.filter((row) => row.reviewed),
    all,
    reviewCount: live.length,
    selectedCount: live.filter((row) => !row.excluded).length,
    total: all.filter((row) => !row.removed).length,
  };
}

/** Tap a photo on To review: in (syncs) ⇄ out (kept in Led AI, not synced). */
export function toggleWalkPhotoExcluded(draft: WalkPhotosDraft, mediaId: string): WalkPhotosDraft {
  const excluded = { ...(draft.excluded ?? {}) };
  if (excluded[mediaId]) delete excluded[mediaId];
  else excluded[mediaId] = true;
  return { ...draft, excluded };
}

/** "4 photos left for Sep 10 daily log" / "Sep 10 daily report photos reviewed". */
export function walkPhotosHeadline(left: number, day: string, integration: WalkPhotosIntegration = "procore"): string {
  const noun = walkPhotosLogNoun(integration);
  const log = day ? `${day} ${noun}` : `the ${noun}`;
  if (left <= 0) return day ? `${day} ${noun} photos reviewed` : `${noun.charAt(0).toUpperCase()}${noun.slice(1)} photos reviewed`;
  return `${left} ${left === 1 ? "photo" : "photos"} left for ${log}`;
}

export function toggleWalkPhotoRemoved(draft: WalkPhotosDraft, mediaId: string): WalkPhotosDraft {
  const removed = { ...draft.removed };
  if (removed[mediaId]) delete removed[mediaId];
  else removed[mediaId] = true;
  return { ...draft, removed };
}

/**
 * "Send 4 photos to Procore" / "Save 4 photos". With nothing selected (every
 * photo kept in Led AI, or none left) Send only settles the step, so it reads
 * Done, as the console's does (LED-768), never "Send 0 photos" (LED-660).
 */
export function walkPhotosSendLabel(total: number, provider: string | null, short = false): string {
  if (total <= 0) return "Done";
  const noun = short ? `${total}` : `${total} photo${total === 1 ? "" : "s"}`;
  return provider ? `Send ${noun} to ${provider}` : `Save ${noun}`;
}

/** "Level 2 › Office 201" → parent "Level 2 › ", leaf "Office 201". */
export function splitLocationPath(name?: string, path?: string): { parent: string; leaf: string } {
  const full = (path || name || "").trim();
  const parts = full.split(/\s*(?:›|>|\/)\s*/).filter(Boolean);
  if (parts.length <= 1) return { parent: "", leaf: name || full };
  const leaf = parts[parts.length - 1];
  return { parent: `${parts.slice(0, -1).join(" › ")} › `, leaf };
}

/**
 * A daily-log Photos card, of any age. Whether it is reviewed on the Walk
 * photos screen or as a card in the review deck is `isWalkPhotosStepCard`
 * (and, on top of that, whether the project is Procore-linked: LED-710).
 */
export function isWalkPhotosCard(item: { scope?: string; category?: string }): boolean {
  return item.scope === "daily_log" && item.category === "photos";
}

/**
 * The instant the Walk photos step went live in PRODUCTION: the finish of
 * Render deploy dep-db4slds9v7es738hhaf0 on ledai_release_standalone
 * (fe2c9eb5a, the first production deploy containing 68345f704). Staging got
 * the step a day earlier (2026-10-09T05:27:44Z, cdf948302); one line has to
 * serve both, and the later one is the one that keeps production's older walks
 * as they were. Staging walks from that one day review their Photos card in
 * the deck — the review they would have had before the step existed.
 *
 * Nothing on a Photos card says which review it was made for: 68345f704
 * changed no creation path, and every provenance field the card carries
 * (mediaAssociationMethod, promptVersion, contextVersion) is found on cards
 * from both sides of this line. The card's own `createdAt` is the property
 * that does separate them (LED-711).
 */
export const WALK_PHOTOS_STEP_LIVE_AT = "2026-10-10T05:19:50Z";
const WALK_PHOTOS_STEP_LIVE_MS = Date.parse(WALK_PHOTOS_STEP_LIVE_AT);

/**
 * The same line for Autodesk projects, which got the step later (LED-766).
 * An Autodesk Photos card made before it is a deck card — the review its walk
 * had — exactly as LED-711 keeps Procore's older cards.
 */
export const WALK_PHOTOS_STEP_AUTODESK_LIVE_AT = "2026-10-16T00:00:00Z";
const WALK_PHOTOS_STEP_AUTODESK_LIVE_MS = Date.parse(WALK_PHOTOS_STEP_AUTODESK_LIVE_AT);

/**
 * Which integration's Walk photos step a project has, from its link: Procore
 * or Autodesk, or none for an unlinked project (LED-710 — nothing syncs, so
 * the step means nothing there).
 */
export function walkPhotosIntegrationOf(provider: string | null | undefined): WalkPhotosIntegration | null {
  return provider === "procore" || provider === "autodesk" ? provider : null;
}

/**
 * A Photos card reviewed on the Walk photos screen (design 5a): made once the
 * step was live. These leave the card-by-card review deck, and the screen runs
 * once nothing else is pending — so they also hold the walk open until Send.
 *
 * An older Photos card is NOT one (LED-711): the step does not apply
 * retroactively, so it stays a card in the deck (Reject · Later · Accept), the
 * review its walk had when it was made. A card with no readable `createdAt`
 * is treated as older: the deck can review any card, the screen cannot.
 *
 * Only on a linked project — Procore, or Autodesk since LED-766 — does the
 * step exist at all (LED-710); this says which cards it takes THERE, from
 * that integration's own live line. Every surface that draws the line must use
 * it, and within one surface the deck filter and the step gate MUST agree: if
 * they disagreed, a Photos card would sit in neither and its walk could never
 * finish. The surfaces: `VisitPage` (`reviewSequence` / `isWalkPhotosStep`),
 * the Review tab queue (`walkBookends.isWalkPhotosRow`) and the office console
 * (`ReviewQueue.isPhotosCardRow`).
 */
export function isWalkPhotosStepCard(
  item: {
    scope?: string;
    category?: string | null;
    createdAt?: Date | string | null;
  },
  integration: WalkPhotosIntegration = "procore",
): boolean {
  if (item.scope !== "daily_log" || item.category !== "photos" || item.createdAt == null) return false;
  const createdMs = new Date(item.createdAt).getTime();
  const liveMs = integration === "autodesk" ? WALK_PHOTOS_STEP_AUTODESK_LIVE_MS : WALK_PHOTOS_STEP_LIVE_MS;
  return Number.isFinite(createdMs) && createdMs >= liveMs;
}

/**
 * A walk photo as the full-screen viewer (`PhotoAnnotationOverlay`) takes it,
 * so tapping a row opens the same annotate/caption screen a record's photo
 * does.
 */
export function walkPhotoMediaRef(photo: WalkPhotoData): ConstructionNoteMediaReference {
  return {
    id: photo.mediaId,
    type: photo.type,
    url: photo.url,
    ...(photo.thumbnailUrl ? { thumbnailUrl: photo.thumbnailUrl } : {}),
    timestamp: new Date(photo.timestamp),
    description: photo.description,
    ...(photo.descriptionPending ? { descriptionStatus: "pending" as const } : {}),
  };
}

/** Where a description edit on this photo is recorded as having been made:
 *  the record that holds it, if any. The row's inline edit sends the same. */
export function walkPhotoEditContext(photo: WalkPhotoData): PhotoCaptionEditContext {
  const home = photo.home;
  return {
    phase: "review",
    surface: "review-stack",
    attachedToLogItem: !!home,
    logItemType: !home ? null : home.kind === "photos" || home.kind === "log" ? "daily_log" : home.kind,
    ...(home?.itemId ? { logItemId: home.itemId } : {}),
  };
}

// ── Walk photos as a review card (design "Walk Photos Options" 7) ────────
// Procore only. The photos still on the step's Photos cards are the ones
// "not on a record"; the card makes one decision for them — daily log +
// gallery, or gallery only — already made from the reviewer's role.

/**
 * The roles whose loose photos default to the daily log: the people who keep
 * it in the field (`ONBOARDING_ROLES` ids). Everyone else defaults to the
 * gallery, with the log a tap away.
 */
const DAILY_LOG_ROLES = new Set(["super", "asst_super", "foreman"]);

export function walkPhotosDefaultDestination(userRole: string | null | undefined): WalkPhotoDestination {
  return userRole && DAILY_LOG_ROLES.has(userRole) ? "log" : "gallery";
}

export type WalkPhotoRecordKind = "OBS" | "PUNCH" | "TASK" | "LOG";

export type WalkPhotosCardModel = {
  /** Still on this step's Photos cards and not left out — what Accept sends. */
  loose: WalkPhotoData[];
  /** Left out with the trash: kept in Led AI, not sent. */
  leftOut: WalkPhotoData[];
  /** Already on another record, time order: shown, never decided here. */
  attached: Array<{ photo: WalkPhotoData; kind: WalkPhotoRecordKind }>;
};

function recordKindOf(kind: WalkPhotoHomeKind): WalkPhotoRecordKind {
  if (kind === "observation") return "OBS";
  if (kind === "punch") return "PUNCH";
  if (kind === "task") return "TASK";
  return "LOG";
}

/** `reviewCardIds` as `buildWalkPhotoRows` takes it: absent, every pending
 *  Photos card is this step's. */
export function buildWalkPhotosCard(
  photos: WalkPhotoData[],
  draft: WalkPhotosDraft,
  reviewCardIds?: ReadonlySet<string> | null,
): WalkPhotosCardModel {
  const sorted = [...photos].sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));
  const underReview = (photo: WalkPhotoData) =>
    isReviewablePhoto(photo) && (!reviewCardIds || reviewCardIds.has(photo.home!.itemId));
  const mine = sorted.filter((photo) => underReview(photo) && !draft.removed[photo.mediaId]);
  return {
    loose: mine.filter((photo) => !draft.excluded[photo.mediaId]),
    leftOut: mine.filter((photo) => !!draft.excluded[photo.mediaId]),
    attached: sorted
      .filter((photo) => photo.home && !underReview(photo))
      .map((photo) => ({ photo, kind: recordKindOf(photo.home!.kind) })),
  };
}

/** "6 photos not on a record". */
export function walkPhotosCardTitle(count: number): string {
  return `${count} ${count === 1 ? "photo" : "photos"} not on a record`;
}

/** The swipe stamp and the receipt: where Accept sends them. */
export function walkPhotosDestinationLabel(destination: WalkPhotoDestination, integration: WalkPhotosIntegration = "procore"): string {
  if (integration === "autodesk") return destination === "log" ? "Report + files" : "Files";
  return destination === "log" ? "Log + gallery" : "Gallery";
}

/** "5:38P" — the tile's corner. */
export function walkPhotoTileTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")}${d.getHours() < 12 ? "A" : "P"}`;
}
